import { useEffect, useState } from 'react';

import { useCurrency } from '@/lib/entities/currency';
import { RentCatalogItem } from '@/lib/entities/rent';

import { CircleButton } from '@/lib/shared/components/Buttons';
import { P2, H3, P4, P3 } from '@/lib/shared/components/Typography';
import { CloseSign, ArrowIcon } from '@/lib/shared/icons';
import { LogoIconSvg } from '@/lib/shared/icons/LogoIcon';
import { SvgChevronLeft } from '@/lib/shared/icons/SvgChevronLeft';
import { IClassname } from '@/lib/shared/model';
import { staticResource } from '@/lib/shared/utils/resources';
import { toMoney } from '@/lib/shared/utils/to-money';

import cn from 'clsx';
import { clsx } from 'clsx';
import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';

import styles from './RentCatalogCard.module.scss';

interface IPropertyCard extends IClassname {
  data: RentCatalogItem;
  theme: 'list' | 'map';
  onClose?: () => void;
  path: string;
  onTouchStart?: (value: string) => void;
  isTouchedCard?: string | null;
  isFirstCard?: boolean;
  isLoading: boolean;
}

export const RentCatalogCard = ({
  data,
  theme,
  onClose,
  path,
  onTouchStart,
  isTouchedCard,
  isFirstCard,
  className,
  isLoading,
}: IPropertyCard) => {
  const [snap, setSnap] = useState(0);
  const [isError, setIsError] = useState(false);
  const { t } = useTranslation('index');
  const {
    currency: { symbol: cur, value },
  } = useCurrency();

  const saleObject = data;

  const districtsString = data.districts
    .map((item) => t(`locations.${item}`))
    .join();
  const location = `${t(`locations.${data.region}`)}, ${districtsString}`;
  const money = saleObject.price ? toMoney(saleObject.price?.[value]) : null;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(max-width: 996px)': {
        active: theme !== 'map',
      },
    },
    loop: true,
  });
  useEffect(() => {
    if (!emblaApi) return;
    const setScrollSnap = (e: UseEmblaCarouselType[1]) => {
      setSnap(e?.selectedScrollSnap() as number);
    };
    emblaApi.on('select', setScrollSnap);
    return () => {
      emblaApi.off('select', setScrollSnap);
    };
  }, [emblaApi]);
  const images = data.images.slice(0, Math.min(data.images.length, 5));
  return (
    <article
      className={cn(
        styles.card,
        theme === 'map' && styles.cardMap,
        isLoading && styles['card--loading'],
        className
      )}
      onTouchStart={() => onTouchStart && onTouchStart(path)}
    >
      {theme === 'map' && (
        <CircleButton
          name="close card"
          as={'button'}
          onClick={onClose}
          content={<CloseSign />}
          theme="mixed"
          className={styles.cardMapClose}
        />
      )}
      <div
        className={cn(
          styles.gallery,
          isTouchedCard === path && styles.gallery_cardActive
        )}
      >
        <div ref={emblaRef} className={styles.card__wrapper}>
          <div className={styles.card__gallery}>
            {images.map((image, i) => (
              <Link
                href={path}
                key={i}
                target="_blank"
                className={styles.card__link}
              >
                <Image
                  className={styles.image}
                  alt=""
                  priority={isFirstCard}
                  onError={function (e) {
                    if (e.type === 'error') {
                      setIsError(true);
                    }
                  }}
                  sizes="(min-width: 1640px) 528px, (min-width: 1460px) 437px, (min-width: 1020px) calc(28.1vw + 32px), (min-width: 980px) calc(-625vw + 6562px), (min-width: 580px) 45vw, calc(100vw - 32px)"
                  src={
                    isError
                      ? '/images/placeholder_image.webp'
                      : staticResource(image) ||
                        '/images/placeholder_image.webp'
                  }
                  width={528}
                  height={358}
                />{' '}
              </Link>
            ))}
          </div>
          {data.managedByVillacarte && (
            <P2
              className={clsx(
                styles.card__managed,
                theme === 'map' && styles['card__managed--map']
              )}
            >
              <LogoIconSvg />
              {t('catalog.propertyManagedByVC')}
            </P2>
          )}
        </div>
        <div
          className={clsx(
            styles.card__nav,
            theme === 'map' && styles['card__nav--map']
          )}
        >
          <CircleButton
            as="button"
            content={<SvgChevronLeft className={styles.card__chevronLeft} />}
            onClick={() => {
              emblaApi?.scrollPrev();
            }}
          />
          <div className={styles.card__pagination}>
            {images.map((src, i) => {
              const isActive = snap === i;
              return (
                <button
                  className={clsx(
                    styles.card__page,
                    isActive && styles['card__page--active']
                  )}
                  key={src}
                  onClick={() => emblaApi?.scrollTo(i)}
                />
              );
            })}
          </div>

          <CircleButton
            as="button"
            content={<SvgChevronLeft />}
            onClick={() => {
              emblaApi?.scrollNext();
            }}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.footer_top}>
          <Link href={path} target="_blank">
            <H3 as="h2" className={styles.title}>
              {saleObject.name}
            </H3>
          </Link>
          <button
            type="button"
            name="close card"
            onClick={onClose && onClose}
            className={styles.closeButton}
          >
            <CloseSign />
          </button>
        </div>

        <div className={theme === 'map' ? styles.cardMapInfo : styles.info}>
          {theme === 'map' && <P4 className={styles.location}>{location}</P4>}
          <div className={styles.mainInfo}>
            <P3>{t('bedrooms')}:</P3>
            <P2>
              {data.bedroomsAmount === 0 ? t('studio') : data.bedroomsAmount}
            </P2>
          </div>
          <div className={styles.mainInfo}>
            <P3>{t('location')}:</P3>
            <P2>{location}</P2>
          </div>
        </div>
        <div
          className={cn(theme === 'map' && styles.cardMapBottom, styles.bottom)}
        >
          {money !== '0' && !!money ? (
            <H3 className={styles.price}>
              {t('prepositions.from')} {cur}
              {money} {t('rentPage.night')}
            </H3>
          ) : (
            <H3 className={styles.price}>{t('rentPage.form.byRequest')}</H3>
          )}
          <CircleButton
            name="go to complex"
            as={Link}
            href={path}
            content={<ArrowIcon isRight />}
            theme={'mixed'}
            className={styles.cardLink}
          />
        </div>
      </div>
    </article>
  );
};
